import React, { useState, useEffect, useMemo, useRef } from 'react';
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import SQLEditor from './components/SQLEditor';
import Dashboard from './components/Dashboard';
import WidgetSettingsModal from './components/WidgetSettingsModal';
import ResultsTable from './components/ResultsTable';
import SeriesColorPicker from './components/SeriesColorPicker';
import AskAI from './components/AskAI';
import { executeRawQuery, fetchWidgets, createWidget, updateWidgetApi, deleteWidgetApi, getTeams, fetchDashboards, createDashboard, deleteDashboardApi, updateDashboardApi } from './services/api';
import './App.css';

const PREDEFINED_QUERIES = [
  {
    name: 'Lead Time Per Team',
    query: `SELECT
  issue.issue_team AS team_name,
  avg((toUnixTimestamp64Milli(deploy.time) - toUnixTimestamp64Milli(issue.issue_created_at)) / 3600000) AS avg_lead_time_hours
FROM metrics AS deploy
INNER JOIN metrics AS issue
  ON deploy.related_issue_id = issue.issue_id
  AND issue.source = 'pm'
WHERE deploy.source = 'git'
  AND deploy.metric = 'branch.time_to_prod'
  AND deploy.related_issue_id IS NOT NULL
  AND issue.issue_created_at IS NOT NULL
  AND issue.issue_team IS NOT NULL
  AND deploy.time >= '{start_date}'
  AND deploy.time <= '{end_date}'
GROUP BY issue.issue_team
LIMIT 10`
  },
  {
    name: 'Lead Time By Story Type',
    query: `SELECT
  issue.provider_type AS issue_type,
  round(avg((toUnixTimestamp64Milli(deploy.time) - toUnixTimestamp64Milli(issue.issue_created_at)) / 3600000), 1) AS avg_lead_time_hours
FROM metrics AS deploy
INNER JOIN metrics AS issue
  ON deploy.related_issue_id = issue.issue_id
  AND issue.source = 'pm'
WHERE deploy.source = 'git'
  AND deploy.metric = 'branch.time_to_prod'
  AND deploy.related_issue_id IS NOT NULL
  AND issue.issue_created_at IS NOT NULL
  AND issue.provider_type IS NOT NULL
  AND deploy.time >= '{start_date}'
  AND deploy.time <= '{end_date}'
GROUP BY issue.provider_type
ORDER BY avg_lead_time_hours DESC
LIMIT 2`
  },
  {
    name: 'Cycle time per issue type',
    query: `SELECT
  issue.provider_type AS issue_type,
  round(avg(cycle.value / 60), 1) AS avg_cycle_time_hours
FROM metrics AS cycle
INNER JOIN metrics AS issue
  ON cycle.related_issue_id = issue.issue_id
  AND issue.source = 'pm'
WHERE cycle.source = 'git'
  AND cycle.metric = 'branch.computed.cycle_time'
  AND cycle.related_issue_id IS NOT NULL
  AND issue.provider_type IS NOT NULL
  AND cycle.time >= '{start_date}'
  AND cycle.time <= '{end_date}'
GROUP BY issue.provider_type
LIMIT 100`
  },
  {
    name: 'Tasks Opened Per Week',
    query: `SELECT
  formatDateTime(toStartOfWeek(time), '%Y-%m-%d') AS week,
  count() AS tasks_opened
FROM metrics
WHERE source = 'pm'
  AND metric = 'pm.issue.state_change'
  AND issue_event_to_value = 'Open'
  AND provider_type = 'Task'
  AND time >= now() - INTERVAL 60 DAY
GROUP BY toStartOfWeek(time)
ORDER BY week ASC
LIMIT 100`
  },
  {
    name: 'Stories/Bugs with AI tools',
    query: `SELECT
  issue.provider_type AS issue_type,
  count(DISTINCT issue.issue_id) AS issues_with_ai,
  count(DISTINCT deploy.pull_request_id) AS prs_with_ai
FROM metrics AS deploy
INNER JOIN metrics AS issue
  ON deploy.related_issue_id = issue.issue_id
  AND issue.source = 'pm'
WHERE deploy.source = 'git'
  AND deploy.related_issue_id IS NOT NULL
  AND issue.provider_type IN ('Story', 'Bug')
  AND length(deploy.dev_tool_names) > 0
  AND deploy.time >= '{start_date}'
  AND deploy.time <= '{end_date}'
GROUP BY issue.provider_type
ORDER BY issues_with_ai DESC
LIMIT 100`
  },
  {
    name: 'Bugs ages in bucket',
    query: `SELECT
  CASE
    WHEN age_days <= 1 THEN '0-1 days'
    WHEN age_days <= 3 THEN '2-3 days'
    WHEN age_days <= 7 THEN '4-7 days'
    WHEN age_days <= 14 THEN '1-2 weeks'
    WHEN age_days <= 30 THEN '2-4 weeks'
    WHEN age_days <= 60 THEN '1-2 months'
    WHEN age_days <= 90 THEN '2-3 months'
    ELSE '3+ months'
  END AS age_bucket,
  count() AS bug_count,
  round(avg(age_days), 1) AS avg_age_days
FROM (
  SELECT
    issue_id,
    provider_key,
    issue_team,
    CASE
      WHEN issue_completed_at IS NOT NULL
      THEN (toUnixTimestamp(issue_completed_at) - toUnixTimestamp(issue_created_at)) / 86400
      ELSE (toUnixTimestamp(now()) - toUnixTimestamp(issue_created_at)) / 86400
    END AS age_days
  FROM metrics
  WHERE source = 'pm'
    AND provider_type = 'Bug'
    AND issue_created_at IS NOT NULL
    AND issue_created_at >= '{start_date}'
    AND issue_created_at <= '{end_date}'
  GROUP BY issue_id, provider_key, issue_team, issue_created_at, issue_completed_at
) AS bug_ages
GROUP BY age_bucket
ORDER BY
  CASE age_bucket
    WHEN '0-1 days' THEN 1
    WHEN '2-3 days' THEN 2
    WHEN '4-7 days' THEN 3
    WHEN '1-2 weeks' THEN 4
    WHEN '2-4 weeks' THEN 5
    WHEN '1-2 months' THEN 6
    WHEN '2-3 months' THEN 7
    WHEN '3+ months' THEN 8
  END
LIMIT 100`
  }
];

function App() {
  const [authenticated, setAuthenticated] = useState(() => {
    return sessionStorage.getItem('authenticated') === 'true';
  });
  const [activeNav, setActiveNav] = useState('metrics');
  const [mode, setMode] = useState('dashboard');
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState(PREDEFINED_QUERIES[0].query);
  const [widgets, setWidgets] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [activeDashboardId, setActiveDashboardId] = useState(() => localStorage.getItem('lastDashboardId'));
  const [showNewDashboard, setShowNewDashboard] = useState(false);
  const [newDashboardName, setNewDashboardName] = useState('');
  const [confirmDeleteDashboard, setConfirmDeleteDashboard] = useState(null);
  const [settingsWidget, setSettingsWidget] = useState(null);
  const widgetSqlSetterRef = useRef(null);
  const widgetSqlRef = useRef(null);
  // Last date with imported data — update this after each data import
  const DATA_END_DATE = '2026-03-14';

  const [dateRange, setDateRange] = useState(() => {
    const end = new Date(DATA_END_DATE);
    const start = new Date(DATA_END_DATE);
    start.setMonth(start.getMonth() - 3);
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
    };
  });
  const [selectedTeam, setSelectedTeam] = useState('');
  const [teams, setTeams] = useState([]);

  const templateVars = useMemo(() => {
    return {
      '{start_date}': dateRange.start,
      '{end_date}': dateRange.end,
      '{team_name}': selectedTeam || '',
    };
  }, [dateRange, selectedTeam]);

  useEffect(() => {
    fetchDashboards()
      .then(dbs => {
        setDashboards(dbs);
        const saved = localStorage.getItem('lastDashboardId');
        const match = saved && dbs.find(d => d.id === saved);
        if (!match && dbs.length > 0) {
          setActiveDashboardId(dbs[0].id);
        }
      })
      .catch(err => console.error('Failed to load dashboards:', err));
    getTeams()
      .then(setTeams)
      .catch(err => console.error('Failed to load teams:', err));
  }, []);

  useEffect(() => {
    if (activeDashboardId) {
      localStorage.setItem('lastDashboardId', activeDashboardId);
      fetchWidgets(activeDashboardId)
        .then(setWidgets)
        .catch(err => console.error('Failed to load widgets:', err));
    } else {
      setWidgets([]);
    }
  }, [activeDashboardId]);
  const [saveTitle, setSaveTitle] = useState('');
  const [saveChartType, setSaveChartType] = useState('bar');
  const [saveSeriesColors, setSaveSeriesColors] = useState({});
  const [detectedSeriesNames, setDetectedSeriesNames] = useState([]);

  const handleLoadSQL = (sql) => {
    if (settingsWidget && widgetSqlSetterRef.current) {
      widgetSqlSetterRef.current(sql);
    } else {
      setQuery(sql);
      setMode('builder');
    }
  };

  const handleRawQueryExecute = async (sql) => {
    setLoading(true);
    setError(null);
    try {
      const data = await executeRawQuery(sql);
      setResults(data);
    } catch (err) {
      setError(err.message || 'Query execution failed');
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToDashboard = async () => {
    if (!saveTitle.trim() || widgets.length >= 9 || !activeDashboardId) return;
    try {
      const newWidget = await createWidget({ title: saveTitle.trim(), sql: query, chartType: saveChartType, dashboard_id: activeDashboardId, seriesColors: Object.keys(saveSeriesColors).length > 0 ? saveSeriesColors : undefined });
      setWidgets(prev => [...prev, newWidget]);
      setSaveTitle('');
      setMode('dashboard');
    } catch (err) {
      console.error('Failed to save widget:', err);
    }
  };

  const handleCreateDashboard = async () => {
    if (!newDashboardName.trim()) return;
    try {
      const db = await createDashboard(newDashboardName.trim());
      setDashboards(prev => [...prev, db]);
      setActiveDashboardId(db.id);
      setNewDashboardName('');
      setShowNewDashboard(false);
    } catch (err) {
      console.error('Failed to create dashboard:', err);
    }
  };

  const handleDeleteDashboard = async () => {
    if (!confirmDeleteDashboard) return;
    const id = confirmDeleteDashboard.id;
    try {
      await deleteDashboardApi(id);
      setDashboards(prev => {
        const remaining = prev.filter(d => d.id !== id);
        if (activeDashboardId === id) {
          setActiveDashboardId(remaining.length > 0 ? remaining[0].id : null);
        }
        return remaining;
      });
    } catch (err) {
      console.error('Failed to delete dashboard:', err);
    } finally {
      setConfirmDeleteDashboard(null);
    }
  };

  const handleDeleteWidget = async (id) => {
    try {
      await deleteWidgetApi(id);
      setWidgets(prev => prev.filter(w => w.id !== id).map((w, i) => ({ ...w, position: i })));
    } catch (err) {
      console.error('Failed to delete widget:', err);
    }
  };

  const handleCloneWidget = async (widget) => {
    try {
      const cloned = await createWidget({
        title: `${widget.title} (copy)`,
        sql: widget.sql,
        chartType: widget.chartType,
        dashboard_id: widget.dashboard_id || activeDashboardId,
        seriesColors: widget.seriesColors,
        unit: widget.unit,
        colSpan: widget.colSpan,
        rowSpan: widget.rowSpan,
      });
      setWidgets(prev => [...prev, cloned]);
    } catch (err) {
      console.error('Failed to clone widget:', err);
    }
  };

  const handleResizeWidget = async (id, colSpan, rowSpan) => {
    try {
      const updated = await updateWidgetApi(id, { colSpan, rowSpan });
      setWidgets(prev => prev.map(w => (w.id === id ? updated : w)));
    } catch (err) {
      console.error('Failed to resize widget:', err);
    }
  };

  const handleReorderWidgets = async (dragId, dropId) => {
    const sorted = [...widgets].sort((a, b) => a.position - b.position);
    const dragIdx = sorted.findIndex(w => w.id === dragId);
    const dropIdx = sorted.findIndex(w => w.id === dropId);
    if (dragIdx === -1 || dropIdx === -1) return;
    const reordered = [...sorted];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    const updated = reordered.map((w, i) => ({ ...w, position: i }));
    setWidgets(updated);
    try {
      await Promise.all(updated.map((w, i) =>
        w.position !== widgets.find(o => o.id === w.id)?.position
          ? updateWidgetApi(w.id, { position: i })
          : Promise.resolve()
      ));
    } catch (err) {
      console.error('Failed to reorder widgets:', err);
    }
  };

  const handleOpenSettings = (widget) => {
    setSettingsWidget(widget);
  };

  const handleSaveSettings = async ({ id, title, sql, chartType, seriesColors, unit }) => {
    try {
      const updated = await updateWidgetApi(id, { title, sql, chartType, seriesColors, unit });
      setWidgets(prev => prev.map(w => (w.id === id ? updated : w)));
      setSettingsWidget(null);
    } catch (err) {
      console.error('Failed to update widget:', err);
    }
  };

  const handleLogin = () => {
    sessionStorage.setItem('authenticated', 'true');
    setAuthenticated(true);
  };

  if (!authenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="app-layout">
      <Sidebar activeItem={activeNav} onItemClick={setActiveNav} />

      <div className="main-content">
        <header className="top-header">
          <div className="header-title">
            <svg width="18" height="18" viewBox="0 0 16 17" fill="none" style={{ color: '#1a2e58' }}>
              <rect x="11.5" y="5.5" width="3" height="10" stroke="currentColor"></rect>
              <rect x="6.5" y="1.5" width="3" height="14" stroke="currentColor"></rect>
              <rect x="1.5" y="9.5" width="3" height="6" stroke="currentColor"></rect>
            </svg>
            <h1>Metrics</h1>
          </div>
          <div className="header-actions">
            <button className="notification-btn">🔔</button>
            <div className="user-avatar">A</div>
          </div>
        </header>

        <div className="content-wrapper">
          <div className="main-panel">
            <div className="mode-toggle">
              <button
                className={mode === 'dashboard' ? 'active' : ''}
                onClick={() => setMode('dashboard')}
              >
                Dashboard
              </button>
              <button
                className={mode === 'builder' ? 'active' : ''}
                onClick={() => setMode('builder')}
              >
                Custom Metric Builder
              </button>
            </div>

            <div className="dashboard-date-picker">
              <label>Date Range:</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={e => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              />
              <span className="date-separator">to</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={e => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              />
              <span className="filter-divider" />
              <label>Team:</label>
              <select
                value={selectedTeam}
                onChange={e => setSelectedTeam(e.target.value)}
              >
                <option value="">All Teams</option>
                {teams.map(team => (
                  <option key={team} value={team}>{team}</option>
                ))}
              </select>
            </div>

            {mode === 'dashboard' && (
              <>
                <div className="dashboard-switcher">
                  <div className="dashboard-tabs">
                    {[...dashboards].sort((a, b) => a.name === 'Default' ? -1 : b.name === 'Default' ? 1 : a.name.localeCompare(b.name)).map(db => (
                      <div
                        key={db.id}
                        className={`dashboard-tab ${db.id === activeDashboardId ? 'active' : ''}`}
                      >
                        <button
                          className="dashboard-tab-btn"
                          onClick={() => setActiveDashboardId(db.id)}
                        >
                          {db.name}
                        </button>
                        {db.id === activeDashboardId && dashboards.length > 1 && db.name !== 'Default' && (
                          <button
                            className="dashboard-tab-delete"
                            onClick={() => setConfirmDeleteDashboard(db)}
                            title="Delete dashboard"
                          >
                            &times;
                          </button>
                        )}
                      </div>
                    ))}
                    {showNewDashboard ? (
                      <div className="dashboard-tab-new-form">
                        <input
                          type="text"
                          value={newDashboardName}
                          onChange={e => setNewDashboardName(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleCreateDashboard();
                            if (e.key === 'Escape') { setShowNewDashboard(false); setNewDashboardName(''); }
                          }}
                          placeholder="Dashboard name"
                          autoFocus
                        />
                        <button className="dashboard-tab-save" onClick={handleCreateDashboard} disabled={!newDashboardName.trim()}>
                          Create
                        </button>
                        <button className="dashboard-tab-cancel" onClick={() => { setShowNewDashboard(false); setNewDashboardName(''); }}>
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button className="dashboard-tab-add" onClick={() => setShowNewDashboard(true)} title="New dashboard">
                        +
                      </button>
                    )}
                  </div>
                </div>
                {activeDashboardId ? (
                  <Dashboard
                    widgets={widgets}
                    onOpenSettings={handleOpenSettings}
                    onDeleteWidget={handleDeleteWidget}
                    onCloneWidget={handleCloneWidget}
                    onResizeWidget={handleResizeWidget}
                    onReorderWidgets={handleReorderWidgets}
                    templateVars={templateVars}
                  />
                ) : (
                  <div className="dashboard-empty">
                    <h3>No dashboards yet</h3>
                    <p>Click the + button above to create your first dashboard.</p>
                  </div>
                )}
              </>
            )}

            {mode === 'builder' && (
              <>
                <div className="query-section">
                  <div className="predefined-queries">
                    <h3>Quick Queries</h3>
                    <ul>
                      {PREDEFINED_QUERIES.map((pq, idx) => (
                        <li key={idx}>
                          <button
                            className="predefined-query-btn"
                            onClick={() => setQuery(pq.query)}
                          >
                            {pq.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="editor-section">
                    <SQLEditor onExecute={handleRawQueryExecute} loading={loading} query={query} setQuery={setQuery} templateVars={templateVars} />
                  </div>
                </div>

                {error && (
                  <div className="error-message">
                    <strong>Error:</strong> {error}
                  </div>
                )}

                {results && activeDashboardId && widgets.length < 9 && (
                  <div className="save-to-dashboard-bar">
                    <input
                      type="text"
                      value={saveTitle}
                      onChange={e => setSaveTitle(e.target.value)}
                      placeholder="Widget title"
                    />
                    <select
                      value={activeDashboardId}
                      onChange={e => setActiveDashboardId(e.target.value)}
                      className="save-dashboard-select"
                    >
                      {dashboards.map(db => (
                        <option key={db.id} value={db.id}>{db.name}</option>
                      ))}
                    </select>
                    <button
                      className="execute-btn"
                      onClick={handleSaveToDashboard}
                      disabled={!saveTitle.trim()}
                    >
                      Save to Dashboard
                    </button>
                  </div>
                )}

                {results && detectedSeriesNames.length > 0 && (
                  <SeriesColorPicker seriesNames={detectedSeriesNames} seriesColors={saveSeriesColors} onChange={setSaveSeriesColors} />
                )}

                {results && (
                  <div className="results-section">
                    <ResultsTable
                      results={results}
                      onViewModeChange={setSaveChartType}
                      seriesColors={saveSeriesColors}
                      onSeriesDetected={({ numericColumns, groupedSeries, stackedSeries }) => {
                        if (saveChartType === 'stacked' && stackedSeries.length > 0) setDetectedSeriesNames(stackedSeries);
                        else if (saveChartType === 'grouped' && groupedSeries.length > 0) setDetectedSeriesNames(groupedSeries);
                        else setDetectedSeriesNames(numericColumns);
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <AskAI onLoadSQL={handleLoadSQL} editingWidget={settingsWidget} widgetSqlRef={widgetSqlRef} />

      {confirmDeleteDashboard && (
        <div className="modal-backdrop" onClick={() => setConfirmDeleteDashboard(null)}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <div className="confirm-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <h3 className="confirm-title">Delete Dashboard</h3>
            <p className="confirm-text">
              Are you sure you want to delete <strong>{confirmDeleteDashboard.name}</strong>? All widgets in this dashboard will be permanently removed.
            </p>
            <div className="confirm-actions">
              <button className="confirm-cancel-btn" onClick={() => setConfirmDeleteDashboard(null)}>Cancel</button>
              <button className="confirm-delete-btn" onClick={handleDeleteDashboard}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {settingsWidget && (
        <WidgetSettingsModal
          widget={settingsWidget}
          isOpen={true}
          onClose={() => setSettingsWidget(null)}
          onSave={handleSaveSettings}
          templateVars={templateVars}
          sqlSetterRef={widgetSqlSetterRef}
          sqlRef={widgetSqlRef}
        />
      )}
    </div>
  );
}

export default App;
